import { ReactNode, useEffect, useState } from 'react'

import { ISelectOption } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useAddQRAScenarioMutation } from 'app/services/api/qra/assessmentScenarios'

import EditScenarioView from './EditScenarioView'
import {
  IQRAScenario,
  IRuleWithId,
  ScenarioOptions,
  WISViewMode
} from './types'
import { useOnCreateValidator } from './utils/useOnCreateWISValidator'
import { selectScenario } from './whatIfScenarioSelectors'
import { setSelectedScenarioSync } from './whatIfScenarioSlice'
import WISModal from './WISModal'

export type ScenarioCreationWrapperType = {
  setViewMode: React.Dispatch<React.SetStateAction<WISViewMode>>
  scenarioOptions: ScenarioOptions
  isModalOpen: boolean
  closeModal: () => void
  canConfirm: boolean
  applyToScenario: (
    applyScenarioCallback: (scenario: IQRAScenario) => void
  ) => void
  selectedThreat: ISelectOption
  cancelButton: ReactNode
  scenarioName: string
  scenarioRules: IRuleWithId[]
  setScenarioRules: React.Dispatch<React.SetStateAction<IRuleWithId[]>>
  setScenarioName: React.Dispatch<React.SetStateAction<string>>
  setSelectedThreat: React.Dispatch<React.SetStateAction<ISelectOption>>
}

const CreateScenarioWrapper = ({
  setViewMode,
  scenarioOptions,
  closeModal,
  isModalOpen,
  canConfirm,
  applyToScenario,
  selectedThreat,
  cancelButton,
  scenarioName,
  setScenarioName,
  scenarioRules,
  setScenarioRules,
  setSelectedThreat
}: ScenarioCreationWrapperType) => {
  const dispatch = useAppDispatch()
  const scenario = useAppSelector(selectScenario)

  const [isScenarioNameEmpty, setIsScenarioNameEmpty] = useState<boolean>(false)

  const [createScenario, { isCreatingScenario, scenarioCreateSuccess }] =
    useAddQRAScenarioMutation({
      selectFromResult: (result) => ({
        isCreatingScenario: result.isLoading,
        scenarioCreateSuccess: result.isSuccess
      })
    })

  useEffect(() => {
    if (scenarioCreateSuccess) {
      setViewMode('run')
    }
  }, [setViewMode, scenarioCreateSuccess])

  const filteredScenarioNames = scenarioOptions
    .filter((option) => option.label !== '')
    .map((option) => option.label)

  const [isScenarioNameValid, scenarioNameValidationMessage] =
    useOnCreateValidator(
      scenarioName,
      filteredScenarioNames,
      isScenarioNameEmpty
    )

  const renderContent: ReactNode = (
    <EditScenarioView
      scenarioName={scenarioName}
      setIsScenarioNameEmpty={setIsScenarioNameEmpty}
      scenarioNameValidationMessage={scenarioNameValidationMessage}
      scenarioModaltitle='New Scenario'
      scenario={scenario}
      scenarioRules={scenarioRules}
      setScenarioRules={setScenarioRules}
      setScenarioName={setScenarioName}
      isScenarioNameValid={isScenarioNameValid}
      selectedThreat={selectedThreat}
      setSelectedThreat={setSelectedThreat}
    />
  )

  const canConfirmScenarioCreation = canConfirm && isScenarioNameValid

  const handleCancel = () => {
    setViewMode('run')
  }

  const handleClose = () => {
    closeModal()
    setViewMode('run')
  }

  const handleCreateScenario = async (scenario: IQRAScenario) => {
    const response = await createScenario(scenario)

    if (response.data != null) {
      dispatch(
        setSelectedScenarioSync({
          id: response.data.id,
          name: scenario.name,
          rules: scenario.rules
        })
      )
    }
  }

  return (
    <WISModal
      isModalOpen={isModalOpen}
      onCancel={handleCancel}
      closeModal={handleClose}
      renderContent={renderContent}
      canConfirm={canConfirmScenarioCreation}
      onConfirm={() => applyToScenario(handleCreateScenario)}
      footerApplyLabel='Save & Apply'
      isLoading={isCreatingScenario}
      cancelButton={cancelButton}
      showRunConfirm={false}
    />
  )
}

export default CreateScenarioWrapper
